import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Dialog from "../../components/ui/Dialog";
import Input from "../../components/ui/Input";
import { phoneRegionsService } from "../../services/phone-regions.service";
import { PhoneRegion, PhoneRegionFormValues } from "../../types/phone-region";
import { useAuth } from "../../context/AuthContext";
import { Permission } from "../../permissions/permission.enum";
import { getErrorMsg } from "../../utils/error";

const EMPTY_FORM: PhoneRegionFormValues = {
  name: "",
  dialCode: "",
  code: "",
  isActive: true,
  otpEnabled: true,
};

const DIAL_CODE_PATTERN = /^\+\d{1,4}$/;
const COUNTRY_CODE_PATTERN = /^[A-Za-z0-9]{2,4}$/;

function validateForm(form: PhoneRegionFormValues, t: (key: string) => string): string {
  if (!form.name.trim()) return t("phone_region_name_required");
  if (!DIAL_CODE_PATTERN.test(form.dialCode.trim())) return t("phone_region_dial_code_invalid");
  if (!COUNTRY_CODE_PATTERN.test(form.code.trim())) return t("phone_region_code_invalid");
  return "";
}

const PhoneRegionsPage = () => {
  const { t } = useTranslation("common");
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(Permission.SYSTEM_SETTINGS_UPDATE);

  const [rows, setRows] = useState<PhoneRegion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PhoneRegion | null>(null);
  const [form, setForm] = useState<PhoneRegionFormValues>(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await phoneRegionsService.list());
    } catch (e) {
      setError(getErrorMsg(e, t("failed_to_load")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY_FORM });
    setFormError("");
    setModalOpen(true);
  };

  const openEdit = (row: PhoneRegion) => {
    setEditTarget(row);
    setForm({
      name: row.name,
      dialCode: row.dialCode,
      code: row.code,
      isActive: row.isActive,
      otpEnabled: row.otpEnabled,
    });
    setFormError("");
    setModalOpen(true);
  };

  const saveRow = async () => {
    const validationError = validateForm(form, t);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    setFormError("");
    try {
      const payload: PhoneRegionFormValues = {
        name: form.name.trim(),
        dialCode: form.dialCode.trim(),
        code: form.code.trim().toUpperCase(),
        isActive: form.isActive,
        otpEnabled: form.otpEnabled,
      };
      if (editTarget) {
        await phoneRegionsService.update(editTarget.id, payload);
      } else {
        await phoneRegionsService.create(payload);
      }
      setModalOpen(false);
      setSuccess(t("saved"));
      await refresh();
    } catch (e) {
      setFormError(getErrorMsg(e, t("failed_to_save")));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setError("");
    setSuccess("");
    try {
      await phoneRegionsService.remove(deleteId);
      setDeleteId(null);
      setSuccess(t("saved"));
      await refresh();
    } catch (e) {
      setError(getErrorMsg(e, t("failed_to_delete")));
    }
  };

  const moveOrder = async (index: number, direction: "up" | "down") => {
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= rows.length) return;
    const updated = rows.map((row, i) => {
      if (i === index) return rows[swapIndex];
      if (i === swapIndex) return rows[index];
      return row;
    });
    try {
      await phoneRegionsService.reorder(updated.map((row, i) => ({ id: row.id, sortOrder: i })));
      setRows(updated);
    } catch (e) {
      setError(getErrorMsg(e, t("failed_to_save")));
    }
  };

  return (
    <Layout title={t("phone_regions")}>
      <div className="space-y-6">
        {error && (
          <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm border border-red-100 dark:border-red-800">
            {error}
          </div>
        )}
        {success && (
          <div className="p-3 rounded-md bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-sm border border-green-100 dark:border-green-800">
            {success}
          </div>
        )}

        <Card className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold">{t("phone_regions")}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t("phone_regions_desc")}
              </p>
            </div>
            {canEdit && (
              <Button variant="primary" onClick={openCreate}>
                {t("add_phone_region")}
              </Button>
            )}
          </div>

          {loading ? (
            <p className="text-sm text-gray-500" aria-live="polite">{t("loading")}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500">{t("noData")}</p>
          ) : (
            <div className="overflow-x-auto themed-scrollbar-prominent">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                    <th className="pb-2 font-medium">{t("phone_region_name")}</th>
                    <th className="pb-2 font-medium">{t("phone_region_dial_code")}</th>
                    <th className="pb-2 font-medium">{t("phone_region_code")}</th>
                    <th className="pb-2 font-medium w-20">{t("faq_sort_order")}</th>
                    <th className="pb-2 font-medium w-20">{t("faq_active")}</th>
                    <th className="pb-2 font-medium w-24">{t("phone_region_otp_enabled")}</th>
                    {canEdit && <th className="pb-2 font-medium w-28">{t("actions")}</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={row.id} className={`border-b dark:border-gray-700 ${row.isActive ? "" : "opacity-50"}`}>
                      <td className="py-2 pr-4">{row.name}</td>
                      <td className="py-2 pr-4 font-mono">{row.dialCode}</td>
                      <td className="py-2 pr-4 font-mono">{row.code}</td>
                      <td className="py-2">
                        {canEdit ? (
                          <div className="flex gap-1">
                            <button
                              onClick={() => moveOrder(idx, "up")}
                              disabled={idx === 0}
                              aria-label={t("move_up")}
                              className="px-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30"
                            >
                              ↑
                            </button>
                            <button
                              onClick={() => moveOrder(idx, "down")}
                              disabled={idx === rows.length - 1}
                              aria-label={t("move_down")}
                              className="px-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30"
                            >
                              ↓
                            </button>
                          </div>
                        ) : (
                          row.sortOrder
                        )}
                      </td>
                      <td className="py-2">{row.isActive ? "✓" : "—"}</td>
                      <td className="py-2">{row.otpEnabled ? "✓" : "—"}</td>
                      {canEdit && (
                        <td className="py-2">
                          <div className="flex gap-2">
                            <button
                              onClick={() => openEdit(row)}
                              className="text-blue-600 hover:underline text-xs"
                            >
                              {t("edit")}
                            </button>
                            <button
                              onClick={() => setDeleteId(row.id)}
                              className="text-red-500 hover:underline text-xs"
                            >
                              {t("delete")}
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editTarget ? t("edit_phone_region") : t("add_phone_region")}
      >
        <div className="space-y-4">
          {formError && (
            <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
              {formError}
            </div>
          )}
          <Input
            label={`${t("phone_region_name")} *`}
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          />
          <div>
            <Input
              label={`${t("phone_region_dial_code")} *`}
              value={form.dialCode}
              placeholder="+852"
              onChange={(e) => setForm((prev) => ({ ...prev, dialCode: e.target.value }))}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t("phone_region_dial_code_hint")}
            </p>
          </div>
          <div>
            <Input
              label={`${t("phone_region_code")} *`}
              value={form.code}
              placeholder="HK"
              maxLength={4}
              onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t("phone_region_code_hint")}
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
            />
            {t("faq_active")}
          </label>
          <div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.otpEnabled}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, otpEnabled: e.target.checked }))
                }
              />
              {t("phone_region_otp_enabled")}
            </label>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t("phone_region_otp_enabled_hint")}
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              {t("cancel")}
            </Button>
            <Button variant="primary" onClick={saveRow} disabled={saving}>
              {saving ? t("saving") : t("save")}
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} title={t("delete")}>
        <div className="space-y-4">
          <p className="text-sm">{t("delete_phone_region_confirm")}</p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteId(null)}>
              {t("cancel")}
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              {t("delete")}
            </Button>
          </div>
        </div>
      </Dialog>
    </Layout>
  );
};

export default PhoneRegionsPage;
